import { IconButton, Loading, Modal } from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { defineMessages, useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';

import styles from './ParishBookView.module.scss';
import {
    ParishBookPersonDto,
    useDeleteParishBookPersonMutation,
    useGetParishBookCategoriesQuery,
    useGetParishBookPersonsQuery,
} from 'api/parishBookEndpoints';
import ViewParishBookPersonDetails from './ViewParishBookPersonDetails/ViewParishBookPersonDetails';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import ConfigurableTanstackTable from 'Components/Common/ConfigurableTanstackTable/ConfigurableTanstackTable';
import AddParishBookPersonView from '../AddParishBookPersonView/AddParishBookPersonView';
import TableSearchInput from 'Components/Common/TableSearchInput/TableSearchInput';
import cx from 'classnames';
import useGetUser from 'hooks/useGetUser';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import CategoryInfoCard from 'Screens/Dashboard/InitialSetup/steps/CategoriesStep/CategoryInfoCard';
import ParishBookCategoryTags from './ParishBookCategoryTags/ParishBookCategoryTags';
import { SpecificParishBookPersonModal } from '../SpecificParishBookPersonView/SpecificParishBookPersonView';
import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const columnHelper = createColumnHelper<ParishBookPersonDto>();

const getColumns = (
    intl: ReturnType<typeof useIntl>,
    categoryMap: Record<string, string>,
    onViewDetails: (id: string) => void,
    onDeleteRequest: (person: ParishBookPersonDto) => void,
    canDelete: boolean,
) => [
    columnHelper.display({
        id: 'name',
        header: () => intl.formatMessage(messages.name),
        cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
    }),
    columnHelper.display({
        id: 'category',
        header: () => intl.formatMessage(messages.category),
        cell: ({ row }) => categoryMap[row.original.categoryId] ?? '-',
    }),
    columnHelper.accessor('phoneNumber', {
        id: 'phone',
        header: () => intl.formatMessage(messages.phone),
        cell: ({ getValue }) => getValue() ?? '-',
    }),
    columnHelper.accessor('email', {
        id: 'email',
        header: () => intl.formatMessage(messages.email),
        cell: ({ getValue }) => getValue() ?? '-',
    }),
    columnHelper.accessor('availability', {
        id: 'availability',
        header: () => intl.formatMessage(messages.availability),
        cell: ({ getValue }) => getValue() ?? '-',
    }),
    columnHelper.accessor('visibleForGuests', {
        id: 'visibleForGuests',
        header: () => intl.formatMessage(messages.visibleForGuests),
        cell: ({ getValue }) => (getValue() ? 'Tak' : 'Nie'),
    }),
    columnHelper.display({
        id: 'interactions',
        header: () => intl.formatMessage(messages.interactions),
        cell: ({ row }) => (
            <div className={styles.interactions}>
                <ViewParishBookPersonDetails
                    id={row.original.id}
                    onViewDetails={onViewDetails}
                />
                {canDelete && (
                    <IconButton
                        kind="ghost"
                        label={intl.formatMessage(messages.deletePerson)}
                        align="bottom"
                        onClick={() => { onDeleteRequest(row.original); }}
                    >
                        <TrashCan />
                    </IconButton>
                )}
            </div>
        ),
    }),
];

const ParishBookView = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const permissions = useGetUserPermission();
    const hasPermission = permissions.isAdmin || permissions.isOwner || permissions.isModerator;
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasSubscriptionAccess = !isFetchingSubscription && isPaidPlan && activeSubscription?.hasAccess !== false;
    const isTopBarActionDisabled = !hasPermission || !hasSubscriptionAccess;
    const {
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    } = useMainContainer();

    const { data: persons, isFetching: isFetchingPersons } = useGetParishBookPersonsQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );
    const { data: categories, isFetching: isFetchingCategories } = useGetParishBookCategoriesQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );

    const [search, setSearch] = useState('');
    const [createPersonOpen, setCreatePersonOpen] = useState(false);
    const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
    const [personToDelete, setPersonToDelete] = useState<ParishBookPersonDto | null>(null);
    const [deletePerson, { isLoading: isDeletingPerson }] = useDeleteParishBookPersonMutation();

    useEffect(() => {
        setTopBarAction(() => () => setCreatePersonOpen(true));
        setTopBarActionTitle(intl.formatMessage(messages.addNew));
        setTopBarIcon(<Add size={20} />);
        setDisabledAction(isTopBarActionDisabled);
        setDisabledActionTooltip(intl.formatMessage(messages.addDisabledTooltip));

        return () => {
            setTopBarAction(null);
            setTopBarActionTitle(undefined);
            setTopBarIcon(undefined);
            setDisabledAction(false);
            setDisabledActionTooltip(undefined);
        };
    }, [
        intl,
        isTopBarActionDisabled,
        setDisabledAction,
        setDisabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
    ]);

    const onCreatePersonClose = () => {
        setCreatePersonOpen(false);
    };

    const categoryMap = useMemo(() => {
        const map: Record<string, string> = {};
        (categories ?? []).forEach((c) => { map[c.id] = c.name; });
        return map;
    }, [categories]);

    const filteredPersons = useMemo(() => {
        const term = search.toLowerCase().trim();
        return (persons ?? [])
            .filter((p) =>
                `${p.firstName}${p.lastName}`.toLowerCase().includes(term));
    }, [persons, search]);

    const hasPersons = filteredPersons.length > 0;

    const columns = useMemo(
        () => getColumns(intl, categoryMap, setSelectedPersonId, setPersonToDelete, hasPermission),
        [intl, categoryMap, hasPermission],
    );

    const isFetching = isFetchingPersons || isFetchingCategories;

    const onDeleteConfirm = async () => {
        if (!currentEntityId || !personToDelete) {
            return;
        }

        await deletePerson({
            entityId: currentEntityId,
            id: personToDelete.id,
        }).unwrap();
        setPersonToDelete(null);
    };

    return (
        <>
            {isFetching && <Loading />}
            <div className={cx(styles.container, !hasPersons && styles.emptyContainer)}>
                <CategoryInfoCard
                    className={styles.infoCard}
                    id="parish-book-info"
                    onlyItems
                    isClosable
                />
                
                {hasPersons && (
                    <TableSearchInput
                        className={styles.search}
                        id="search-parish-book"
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={search}
                        onChange={setSearch}
                    />
                )}
                <ConfigurableTanstackTable
                    className={styles.tableWrapper}
                    data={filteredPersons}
                    columns={columns}
                    getRowId={(row) => row.id}
                    rowClassName={() => styles.row}
                    emptyStateTitle={intl.formatMessage(messages.emptyState)}
                />
                <section className={styles.categoriesSection}>
                    <ParishBookCategoryTags />
                </section>
                <AddParishBookPersonView open={createPersonOpen} onClose={onCreatePersonClose} />
                <SpecificParishBookPersonModal
                    personId={selectedPersonId}
                    open={Boolean(selectedPersonId)}
                    onClose={() => { setSelectedPersonId(null); }}
                />
                <Modal
                    open={Boolean(personToDelete)}
                    danger
                    modalHeading={intl.formatMessage(messages.deletePersonConfirmation, {
                        name: personToDelete ? `${personToDelete.firstName} ${personToDelete.lastName}` : '',
                    })}
                    modalLabel={intl.formatMessage(sharedMessages.irreversibleAction)}
                    primaryButtonText={intl.formatMessage(sharedMessages.remove)}
                    secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
                    closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                    primaryButtonDisabled={isDeletingPerson}
                    loadingStatus={isDeletingPerson ? 'active' : undefined}
                    onRequestClose={() => { setPersonToDelete(null); }}
                    onRequestSubmit={onDeleteConfirm}
                />
            </div>
        </>
    );
};

const ParishBookViewHandler = () => {
    const user = useGetUser();
        
    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    return <ParishBookView />;
};

const messages = defineMessages({
    description: {
        id: 'parishBook.description',
        defaultMessage: 'Lista osób przypisanych do parafii.',
    },
    searchPlaceholder: {
        id: 'parishBook.search',
        defaultMessage: 'Szukaj po imieniu, nazwisku lub funkcji',
    },
    addNew: {
        id: 'parishBook.addNew',
        defaultMessage: 'Dodaj nową osobę',
    },
    addDisabledTooltip: {
        id: 'parishBook.addDisabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do dodania kontaktu parafialnego.',
    },
    name: {
        id: 'parishBook.table.name',
        defaultMessage: 'Imię i nazwisko',
    },
    role: {
        id: 'parishBook.table.role',
        defaultMessage: 'Funkcja',
    },
    category: {
        id: 'parishBook.table.category',
        defaultMessage: 'Rola lub funkcja',
    },
    phone: {
        id: 'parishBook.table.phone',
        defaultMessage: 'Telefon',
    },
    email: {
        id: 'parishBook.table.email',
        defaultMessage: 'Email',
    },
    availability: {
        id: 'parishBook.table.availability',
        defaultMessage: 'Dostępność',
    },
    visibleForGuests: {
        id: 'parishBook.table.visibleForGuests',
        defaultMessage: 'Dla Parafian',
    },
    interactions: {
        id: 'parishBook.table.interactions',
        defaultMessage: 'Interakcje',
    },
    deletePerson: {
        id: 'parishBook.table.deletePerson',
        defaultMessage: 'Usun kontakt',
    },
    deletePersonConfirmation: {
        id: 'parishBook.table.deletePersonConfirmation',
        defaultMessage: 'Czy na pewno chcesz usunac kontakt {name}?',
    },
    emptyState: {
        id: 'parishBook.table.emptyState',
        defaultMessage: 'Brak osób do wyświetlenia.',
    },
});

export default ParishBookViewHandler;
